import {useMutation, UseMutationOptions, UseMutationResult} from '@tanstack/react-query';
import {useCallback} from 'react';

import {useApiHooks} from '../../api-hook/api-hook';

import {
    fetchExportConcordance,
    fetchReviewConcordance,
    fetchReviewConcordanceSentiment,
} from './review-concordance-api';
import {
    ExportExcelType,
    FetchReviewConcordanceResultType,
    ReviewConcordanceRequestType,
    ReviewConcordanceSentimentRequestType,
    ReviewConcordanceSentimentType,
    UseReviewConcordanceSentimentType,
    UseReviewConcordanceType,
} from './review-concordance-type';

export function useReviewConcordance(): UseReviewConcordanceType {
    const {isInProgress, setIsInProgress, processError, setProcessError, result, setResult, reset} =
        useApiHooks<FetchReviewConcordanceResultType>();

    const fetch = useCallback(
        (options: ReviewConcordanceRequestType, mainFilterKey: string) => {
            const {
                mode,
                brandIds: competitorBrandIds,
                q,
                startDate,
                endDate,
                contextSize,
                page,
                pageSize,
                sentiments,
                sources,
                tags,
                phrases,
                topics,
                rating,
                withAnswers,
                target,
                useDeleted,
                userTagsIds,
                aspectIds,
            } = options;

            if (!q) {
                return;
            }

            setIsInProgress(true);
            setProcessError(null);

            fetchReviewConcordance(
                {
                    q,
                    startDate,
                    endDate,
                    contextSize,
                    page,
                    pageSize,
                    sentiments,
                    sources,
                    tags,
                    phrases,
                    topics,
                    rating,
                    withAnswers,
                    target,
                    useDeleted,
                    userTagsIds,
                    aspectIds,
                    mode,
                    brandIds: competitorBrandIds,
                },
                mainFilterKey
            )
                .then(setResult)
                .finally(() => setIsInProgress(false))
                .catch(setProcessError);
        },
        [setIsInProgress, setProcessError, setResult]
    );

    return {fetch, isInProgress, processError, result, reset};
}

export function useReviewConcordanceSentiment(): UseReviewConcordanceSentimentType {
    const {isInProgress, setIsInProgress, processError, setProcessError, result, setResult, reset} =
        useApiHooks<ReviewConcordanceSentimentType>();

    const fetch = useCallback(
        (options: ReviewConcordanceSentimentRequestType, mainFilterKey: string) => {
            const {
                q,
                mode,
                brandIds: competitorBrandIds,
                startDate,
                endDate,
                contextSize,
                sentiments,
                sources,
                tags,
                phrases,
                topics,
                rating,
                withAnswers,
                target,
                useDeleted,
                userTagsIds,
                aspectIds,
            } = options;

            if (!q) {
                return;
            }

            setIsInProgress(true);
            setProcessError(null);

            fetchReviewConcordanceSentiment(
                {
                    q,
                    startDate,
                    endDate,
                    contextSize,
                    sentiments,
                    sources,
                    tags,
                    phrases,
                    topics,
                    rating,
                    withAnswers,
                    target,
                    useDeleted,
                    userTagsIds,
                    aspectIds,
                    mode,
                    brandIds: competitorBrandIds,
                },
                mainFilterKey
            )
                .then(setResult)
                .finally(() => setIsInProgress(false))
                .catch(setProcessError);
        },
        [setIsInProgress, setProcessError, setResult]
    );

    return {fetch, isInProgress, processError, result, reset};
}

export function useExportConcordance(
    options?: UseMutationOptions<unknown, unknown, ExportExcelType>
): UseMutationResult<unknown, unknown, ExportExcelType> {
    return useMutation(['export-concordance'], (parameters) => fetchExportConcordance(parameters), options);
}
